import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal, NgbDateStruct, NgbInputDatepicker, NgbModal, NgbTypeahead} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {debounceTime, distinctUntilChanged, map, Observable, of, Subscription, switchMap} from 'rxjs';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {PlanningApiService} from "../../../../services/api/planning-api.service";
import {ToastService} from "../../../../services/toaster.service";
import {BaseComponent} from "../../../base/base.component";
import {ReservationDetails} from "../../../../models/reservation_details.model";
import {
  CINEGO,
  COMMERCIAL,
  CREATED,
  FORFAIT,
  GC,
  GC_ABONNEES,
  LIST_STATUS,
  LIST_TVA,
  LIST_TYPE_CONTRACT,
  LIST_TYPE_COPIE,
  LIST_TYPE_FACTURE,
  LOAN,
  RECEIVED_PROVIDER,
  RECEIVED_SITE,
  REQ_DIGITAL_SEND,
  REQ_DUPLIC_SEND,
  SENT,
  TRANSPORT_STATUS,
  WAITING
} from "../../../../consts/status.const";
import {DatePipe, KeyValuePipe, NgClass, NgForOf, NgIf, NgStyle} from "@angular/common";
import {CirculationApiService} from "../../../../services/api/circulation-api.service";
import {Cinema, CinemaAssocie} from "../../../../models/cinema.model";
import {dateUtils} from "../../../../helpers/date.utils";
import {Version, VersionPrefDetails} from "../../../../models/transport.model";
import {PlanningRegionTauxModalComponent} from "../planning-region-taux-modal/planning-region-taux-modal.component";
import {PlanningRegionApiService} from "../../../../services/api/planning-region-api.service";
import {catchError} from "rxjs/operators";
import {SearchApiService} from "../../../../services/api/search-api.service";
import {Societe} from "../../../../models/societe.model";
import {ReleasePlanApiService} from "../../../../services/api/release-plan-api.service";

import {
  ContractCommentModalComponent
} from "../planning-region-contract-comment-modal/planning-region-contract-comment-modal.component";
import {TransportApiService} from "../../../../services/api/transport-api.service";
import {ProgressSpinnerComponent} from "../../../progress-spinner/progress-spinner.component";

@Component({
  selector: 'app-edit-reservation-modal',
  templateUrl: './planning-region-edit-reservation-modal.component.html',
  standalone: true,
  providers: [DatePipe],
  imports: [
    NgIf,
    NgForOf,
    TranslateModule,
    ReactiveFormsModule,
    NgbInputDatepicker,
    NgClass,
    KeyValuePipe,
    FormsModule,
    DatePipe,
    NgStyle,
    NgbTypeahead,
    ProgressSpinnerComponent
  ],
  styleUrls: ['./planning-region-edit-reservation-modal.component.scss']
})
export class PlanningRegionEditReservationModalComponent extends BaseComponent implements OnDestroy, OnInit {
  @Input() id_reser: number = 0;
  @Input() title: string = '';
  @Input() date!: NgbDateStruct;
  reservation?: ReservationDetails;
  contractForm: FormGroup;
  contractTypes = LIST_TYPE_CONTRACT;
  copyTypes = LIST_TYPE_COPIE;
  listStatus: any = LIST_STATUS;
  listTva = LIST_TVA;
  listTypeFacture: any = LIST_TYPE_FACTURE;
  transportStatuses : any = TRANSPORT_STATUS;

  cinemas?: Cinema[] = [];
  versionsPref: VersionPrefDetails[] = [];
  versions: Version[] = [];
  cinema_associe: CinemaAssocie[] = [];
  pdc_reser: number | null = null;
  showPopup: boolean = false;
  createdTransport: number = 0;
  processTransport: number = 0;
  sendTransport: number = 0;
  loading: boolean = true;
  dateDebutControl = new FormControl();
  dateFinControl = new FormControl();

  protected readonly commercial = COMMERCIAL;
  protected readonly forfait = FORFAIT;
  protected readonly loan = LOAN;
  protected readonly cinego = CINEGO;
  protected readonly gc = GC;
  protected readonly gc_abonnes = GC_ABONNEES;
  protected readonly Array = Array;
  protected readonly REQ_DIGITAL_SEND = REQ_DIGITAL_SEND;
  protected readonly CINEGO = CINEGO;
  protected readonly RECEIVED_SITE = RECEIVED_SITE;
  protected readonly CREATED = CREATED;
  protected readonly REQ_DUPLIC_SEND = REQ_DUPLIC_SEND;

  private subscriptions: Subscription = new Subscription();
  private reloadReserDetailsSubscription = new Subscription();

  constructor(
    public activeModal: NgbActiveModal,
    private planningApiService: PlanningApiService,
    private toastService: ToastService,
    private translate: TranslateService,
    private circulationApiService: CirculationApiService,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private planningRegionApiService: PlanningRegionApiService,
    private searchService: SearchApiService,
    private releasePlanApiService: ReleasePlanApiService,
    private transportApiService: TransportApiService,
  ) {
    super();
    this.loading = true;
    this.contractForm = this.fb.group({
      type_contract: [null, Validators.required],
      minimum: [0],
      copyType: [''],
      adrc: [false],
      pdc: [false],
      reservationStatus: [6],
      forfait: [0],
      coef_mult: [1],
      invoice_type: ['cine'],
      avpHour: ['', [Validators.min(0), Validators.max(23)]],
      avpMinute: ['', [Validators.min(0), Validators.max(59)]],
      cinema_associe: [null],
      pdc_reser: [null],
      societe: [''],
      adresse: [''],
      codePostal: [''],
      pays: [''],
      numero_engagement: ['']
    });
  }

  ngOnInit(): void {
    this.setupValuesChangeListeners();
    this.reloadReserDetailsSubscription = this.planningRegionApiService.reloadReserDetails$.subscribe((data) => {
      this.getReservation(this.id_reser);
    });
    this.subscriptions.add(this.reloadReserDetailsSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getReservation(idReservation: number): void {
    const subscription = this.planningApiService.getReservation(this.cookies, idReservation)
      .subscribe(
        (reservation) => {
          this.reservation = reservation;
          this.reservation.is_chorus = true;
          this.initializeForm();
          this.loading = false;
        },
        () => {
          this.loading = false;
          this.toastService.showDanger(this.translate.instant('toast.error'));
        }
      );
    this.subscriptions.add(subscription);
  }


  initializeForm(): void {
    if (this.reservation && this.reservation.contract && this.reservation.reser) {
      const heureAvp = this.reservation.reser.heure_avp || '';
      const [avpHour, avpMinute] = heureAvp.split(':').map(value => value.replace("'", ""));

      this.dateDebutControl.setValue(dateUtils.parseDateToNgbDateStruct(this.reservation.reser.date_debut) || '' );
      this.dateFinControl.setValue(dateUtils.parseDateToNgbDateStruct(this.reservation.reser.date_fin) || '' );

      this.cinema_associe = this.reservation.contract.type_copie.cinema_associe;
      this.contractForm.patchValue({
        invoice_type: 'cine',
        type_contract: this.reservation.contract.type_contract,
        minimum: this.reservation.contract.minimum || 0,
        copyType: this.reservation.contract.type_copie.type || '',
        adrc: this.reservation.contract.is_adrc,
        coef_mult: this.reservation.contract.coef_mult,
        pdc: this.reservation.contract.is_pdc,
        reservationStatus: this.reservation.contract.statut,
        forfait: this.reservation.contract.forfait,
        numero_engagement: this.reservation.contract.numero_engagement,
        avpHour: avpHour || '',
        avpMinute: avpMinute || '',
        pdc_reser: null
      });
      this.getVersions();
      this.getTransportStatus();
    }
  }

  private getVersions() {
    if (this.reservation && this.reservation.contract && this.reservation.reser) {
      this.versionsPref = Object.values(this.reservation.reser.ver_pref).map(pref => pref.ver as VersionPrefDetails);

      this.versions = Array.isArray(this.reservation.versions)
        ? this.reservation.versions
        : Object.values(this.reservation.versions);

      this.versions.forEach(version => {
        version.checked = false;
        const matchingVersionPref = this.versionsPref.find(pref => pref.id_ver === version.id_ver);

        if (matchingVersionPref) {
          version.checked = matchingVersionPref.checked;
        }
      });
    }
  }

  onDateChange(value: NgbDateStruct, type: 'start' | 'end'): void {

    this.planningApiService.changeReservationDate(
      this.cookies,
      this.id_reser,
      dateUtils.formatDateStruct(dateUtils.toDate(value)),
      type
    ).subscribe(
      (response) => {
        if (response.error) {
          this.toastService.showDanger(response.error);
        } else {
          this.toastService.showSuccess(this.translate.instant('toast.success'));
        }
      },
      (error) => {
        this.toastService.showDanger(this.translate.instant('toast.error'));
      }
    );
  }

  setupValuesChangeListeners() {

    const pdcSub = this.contractForm.get('pdc')?.valueChanges.subscribe((pdcChecked: boolean) => {
      if (pdcChecked) {
        this.onPdcChange(pdcChecked);
      }

    });

    const dateDebut = this.dateDebutControl.valueChanges.subscribe(date => {
      const date_debut = dateUtils.parseDateToNgbDateStruct(this.reservation?.reser.date_debut || '')
      if (date.year !== date_debut.year || date.month !== date_debut.month || date.day !== date_debut.day) {
        this.onDateChange(date, 'start');
      }
    });

    const dateFin = this.dateFinControl.valueChanges.subscribe(date => {
      const date_fin = dateUtils.parseDateToNgbDateStruct(this.reservation?.reser.date_fin || '')
      if (date.year !== date_fin.year || date.month !== date_fin.month || date.day !== date_fin.day) {
        this.onDateChange(date, 'end');
      }
    });

    this.subscriptions.add(pdcSub);
    this.subscriptions.add(dateDebut);
    this.subscriptions.add(dateFin);
  }

  onPdcChange(pdcChecked: boolean): void {
    if (pdcChecked && this.reservation) {
      const params = {
        id_film: this.reservation.film.id_film,
        wedn: dateUtils.formatDateStruct(dateUtils.toDate(this.date)),
        circulation: false
      };
      const subscription = this.circulationApiService.getCinemas(this.cookies, params).subscribe(
        (response) => {
          this.cinemas = response.cinemas;
        },
        () => this.toastService.showDanger(this.translate.instant('toast.error'))
      );
      this.subscriptions.add(subscription);
    } else {
      this.cinemas = [];
    }
  }


  updateTaux(event: Event, id_week: number): void {
    const inputElement = event.target as HTMLInputElement;
    const tauxValue = inputElement.value;
    const taux =  [{id_week: id_week, taux: tauxValue}]

    this.planningApiService.updateTaux(this.cookies, taux).subscribe(
      (response) => {
        this.toastService.showSuccess('toast.success');
        this.planningRegionApiService.reloadPlanningRegion(this.idRegion);
        this.planningRegionApiService.reloadDetailsReservation(this.id_reser);
      },
      (error) => {
        this.toastService.showDanger('toast.error');
      }
    );
  }

  updateContract(): void {
    if (this.contractForm.valid) {

      if (this.contractForm.value.forfait == 0 && this.contractForm.value.type_contract == this.forfait) {
        this.toastService.showDanger(this.translate.instant('toast.reservationForfaitZeo'));
        return;
      }
      if (this.contractForm.value.pdc && this.contractForm.value.pdc_reser === null && this.contractForm.value.type_contract === this.commercial) {
        this.toastService.showDanger(this.translate.instant('toast.reservationPdcReserNull'));
        return;
      }

      let queryParams = {}
      if (this.contractForm.value.type_contract == this.forfait) {

        queryParams = {
          type_contract: this.contractForm.value.type_contract,
          id_contract	: this.reservation?.contract.id_contract,
          numero_engagement	: this.contractForm.value.numero_engagement,
          forfait	: this.contractForm.value.forfait,
          factu_choice: this.contractForm.value.invoice_type,
          coef_mult	: this.contractForm.value.coef_mult
        };
      }
      else if (this.contractForm.value.type_contract == this.commercial){
        if (this.cinemas) {
          queryParams = {
            type_contract: this.contractForm.value.type_contract,
            id_contract: this.reservation?.contract.id_contract,
            numero_engagement: this.contractForm.value.numero_engagement,
            minimum: this.contractForm.value.minimum,
            type_copie: this.contractForm.value.copyType,
            is_adrc: this.contractForm.value.adrc.toString(),
            is_pdc: this.contractForm.value.pdc.toString(),
            pdc_reser: this.getPdc()
          };
        }
      }
      if (this.contractForm.value.type_contract == this.forfait || this.contractForm.value.type_contract == this.commercial) {
        this.updateForfaitOrCommercialContract(queryParams)
      }
      else if (this.contractForm.value.type_contract == this.loan && this.reservation?.contract) {
        this.updateLoanContract()
      }
    }
    this.closeModal();
  }

  private getPdc(): number | null {

    if (this.reservation
      && this.reservation.contract
      && this.cinemas && this.cinemas.length > 0) {

      if (this.contractForm.value.pdc_reser) {
        return this.contractForm.value.pdc_reser;
      } else if (Array.isArray(this.reservation?.contract?.type_copie.cinema_associe)) {
        const foundCinema = this.cinemas.find(cine => cine.id_cinema === this.reservation?.contract?.type_copie.cinema_associe.slice(-1)[0].ID);
        return foundCinema ? foundCinema.id_reser : null;
      }
    }

    return null;
  }

  private updateForfaitOrCommercialContract(queryParams: any): void {
    this.planningApiService.updateContractInfos(this.cookies, queryParams).subscribe({
      next: () => {
        this.toastService.showSuccess('toast.success');
        this.planningRegionApiService.reloadPlanningRegion(this.idRegion);
        this.planningRegionApiService.reloadDetailsReservation(this.id_reser);
      },
      error: () => {
        this.toastService.showDanger('toast.error');
      }
    });
  }

  private updateLoanContract(): void {
    const id_contract = this.reservation?.contract?.id_contract;

    if (id_contract) {
      this.planningApiService.updateLoanContract(this.cookies, id_contract, this.id_reser).subscribe({
        next: () => {
          this.toastService.showSuccess('toast.success');
          this.planningRegionApiService.reloadPlanningRegion(this.idRegion);
          this.planningRegionApiService.reloadDetailsReservation(this.id_reser);
        },
        error: () => {
          this.toastService.showDanger('toast.error');
        }
      });
    }
  }


  updateTime(): void {
    const avpHour = this.contractForm.get('avpHour')?.value;
    const avpMinute = this.contractForm.get('avpMinute')?.value;

    if (avpHour !== '' && avpMinute !== '') {
      const timeString = `${avpHour}:${avpMinute}`;
      this.planningApiService.setAvpHour(this.cookies, this.id_reser, timeString).subscribe({
        next: () => {
          this.toastService.showSuccess('toast.success');
        },
        error: () => {
          this.toastService.showDanger('toast.error');
        }
      });
    }
  }

  toggleVersion(version: Version): void {

    version.checked = !version.checked;
    this.planningApiService.changeVersion(this.cookies, version.id_ver, this.id_reser, version.checked).subscribe(
      (response) => {
        this.toastService.showSuccess('toast.success');
        this.planningRegionApiService.reloadPlanningRegion(this.idRegion);
        this.planningRegionApiService.reloadDetailsReservation(this.id_reser);

      },
      (error) => {
        this.toastService.showDanger('toast.error');
      }
    );
  }

  closeModal(): void {
    this.activeModal.close();
  }

  updateTransportStatus(event: Event, id_transport: number){

    const inputElement = event.target as HTMLSelectElement;
    const status = inputElement.value;

    this.transportApiService.updateStatusFollowDistri(this.cookies, id_transport, status).subscribe({
      next: (response) => {
        this.toastService.showSuccess('toast.success');
        this.planningRegionApiService.reloadPlanningRegion(this.idRegion);
        this.planningRegionApiService.reloadDetailsReservation(this.id_reser);
      },
      error: (error) => {
        this.toastService.showDanger('toast.error');
      },
    });

  }
  updateTransportType(event: Event, id_transport: number, id_pkg: number, presta_dest: boolean = false): void {
    const inputElement = event.target as HTMLSelectElement;
    const value = parseInt(inputElement.value, 10);

    const params = {
      type_transport: presta_dest ? 3 : value,
      id_transport: id_transport,
      presta_dest: presta_dest ? value : null,
    };

    this.planningApiService.updateTransport(this.cookies, params)
      .subscribe(
        () => {

        this.planningRegionApiService.reloadPlanningRegion(this.idRegion);
        this.planningRegionApiService.reloadDetailsReservation(this.id_reser);

        },
        () => this.toastService.showDanger(this.translate.instant('toast.error'))
      );
  }


  getTransportStatus(){

    this.createdTransport = 0;
    this.processTransport = 0;
    this.sendTransport = 0;
    for(const id_pkg in this.reservation?.list_transport){
      if (this.reservation?.list_transport[id_pkg].req.etat == CREATED){
        this.createdTransport += 1;
      }
      if (this.reservation?.list_transport[id_pkg].req.etat == RECEIVED_PROVIDER || this.reservation?.list_transport[id_pkg].req.etat == WAITING){
        this.processTransport += 1;
      }
      if (this.reservation?.list_transport[id_pkg].req.etat == SENT){
        this.sendTransport += 1;
      }
    }
  }

  openTauxModal() {
    if (this.reservation?.contract){
      const modalRef = this.modalService.open(PlanningRegionTauxModalComponent);
      modalRef.componentInstance.taux = this.reservation?.contract.taux;
      modalRef.componentInstance.title = 'tauxModal.modalHeader.title';
      modalRef.componentInstance.updateTaux = (event: any, idWeek: number) => {
        this.updateTaux(event, idWeek);
      };
    }
  }

  searchSociete = (text$: Observable<string>): Observable<any[]> => {
    return text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term =>
        this.searchService.searchByTerm(this.cookies,
          { query_string: term,
            donnees: 'societes',
            type_search: 'societes_exploitant'
          }).pipe(
          map(res => res.items),
          catchError(() => of([]))
        )
      )
    );
  }

  formatter = (societe: Societe) => `${societe.nom_societe} - ${societe.liv_ville} - (${societe.liv_code_postal.substring(0, 2)})`;

  onFilmSelect(event: any): void {
    this.searchSociete = event.item;
  }

  getStatusClass(status: number): string {
    return `bg-status${status}`;
  }

  updateReservationState(): void {
    const stateReservation = this.contractForm.get('reservationStatus')?.value;

    this.releasePlanApiService.changeStateReservation(this.cookies, this.id_reser, stateReservation).subscribe({
      next: (response) => {
        this.toastService.showSuccess('toast.success');
        this.planningRegionApiService.reloadPlanningRegion(this.idRegion);
        this.planningRegionApiService.reloadDetailsReservation(this.id_reser);
      },
      error: (error) => {
        this.toastService.showDanger('toast.error');
      },
    });
  }

  openCommentModal(): void {
    const modalRef = this.modalService.open(ContractCommentModalComponent,
      {
        size: 'lg',
        centered: false,
      }
      );
    modalRef.componentInstance.comments = this.reservation?.contract.comment_contract;
  }

  hasComments(): boolean {
    if (this.reservation?.contract) {
      return this.reservation.contract.comment_contract.some(comment => (comment.commentaires !== null && comment.commentaires !== ''));
    }
    return false;
  }

  validateTransport(idTransport: number): void {
    this.planningApiService.validateTransport(this.cookies, idTransport).subscribe({
      next: (response) => {
        this.toastService.showSuccess('toast.success');
        this.planningRegionApiService.reloadPlanningRegion(this.idRegion);
        this.planningRegionApiService.reloadDetailsReservation(this.id_reser);
      },
      error: (error) => {
        this.toastService.showDanger('toast.error');
      },
    });
  }


}
